<template>
  <div id="project-list" :class="{ light: !darkMode }">
    <div
      class="project text"
      :class="{ selected: project.shortName === selectedProject }"
      v-for="(project, index) in projectList"
      :key="index"
      @click="selectProject(project.shortName)"
    >
      {{ shouldShowShortName ? project.shortName : project.name }}
    </div>
  </div>
  <div
    id="project-container"
    ref="resizeElement"
    :class="{
      light: !darkMode,
      hasScrollBar: hasScrollBar,
    }"
  >
    <div class="title" :class="{ light: !darkMode }">
      {{ getSelectedProject?.name }}
    </div>
    <img
      id="screen"
      :src="getSelectedProject?.imgPath"
      @click="openLink(getSelectedProject?.link || null)"
    />
    <div id="description" class="text">
      {{ $t("project." + getSelectedProject?.tradsKey + "1") }}
    </div>
    <div
      id="more-description"
      class="text"
      :class="{ extended: isExpanded }"
      v-html="$t('project.' + getSelectedProject?.tradsKey + '2')"
    ></div>
    <div id="see-more" class="text" @click="toggleText">
      {{ isExpanded ? $t("SL") : $t("SM") }}
    </div>
    <div id="stack">STACKS : {{ getSelectedProject?.stack }}</div>
  </div>
</template>
<script lang="ts">
import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  ref,
  computed,
} from "vue";
import store from "@/store";

interface Project {
  name: string;
  shortName: string;
  tradsKey: string;
  imgPath: string;
  stack: string;
  link: string;
}

export default defineComponent({
  name: "Project",
  data(): {
    projectList: Project[];
    selectedProject: string;
    isExpanded: boolean;
    hasScrollBar: boolean;
    observer: ResizeObserver | null;
  } {
    return {
      projectList: [
        {
          name: "Camille Fournet Bracelets",
          shortName: "CF Bracelets",
          tradsKey: "CFB",
          imgPath: "CFB.png",
          stack: "NodeJS, VueJS, TypeScript, JavaScript, Azure, AdobeXD",
          link: "https://www.camillefournet.com/fr/module/ya_configurator/Configurator3D?code=FB-BO.4-LA.9-LB.B-LO.6-LG.A-MT.1-PO.1-RE.1-EP.5-FC.25-TA.0007-DT.1-FT.5-PA.2-FI.01-TR.01-MF.034-MD.252-CA.006-CD.194-IN.___&currency=EUR&language=fr&configurator=bracelet",
        },
        {
          name: "Camille Fournet Ceintures",
          shortName: "CF Ceintures",
          tradsKey: "CFC",
          imgPath: "CFC.png",
          stack: "NodeJS, VueJS, TypeScript, JavaScript, Azure, AdobeXD",
          link: "https://www.camillefournet.com/fr/module/ya_configurator/Configurator3D?code=CC-BO.07-LO.36-MT.1-PO.1-TS.3-PA.2-FE.01-FI.01-CI.01-TR.01-MF.318-MD.318-CA.001-CD.104-IN._____&currency=EUR&language=fr&configurator=belt",
        },
        {
          name: "Globe Trotteur Valises",
          shortName: "GT Valises",
          tradsKey: "GTV",
          imgPath: "GTV.png",
          stack: "NodeJS, VueJS, TypeScript, JavaScript, Azure, Figma",
          link: "https://www.globe-trotter.com/pages/custom-4-wheels-large-check-in",
        },
      ],
      selectedProject: "CF Bracelets",
      isExpanded: false, // État de la zone de texte
      hasScrollBar: false,
      observer: null, // Stocker l'observer
    };
  },
  computed: {
    darkMode() {
      return store.state.darkMode;
    },
    getSelectedProject(): Project | undefined {
      return this.projectList.find(
        (project: Project) => project.shortName === this.selectedProject
      );
    },
  },
  setup() {
    // return true in shouldShowShortName if width screen < 768
    const windowWidth = ref(window.innerWidth);
    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
    };
    const shouldShowShortName = computed(() => windowWidth.value < 768);

    onMounted(() => {
      window.addEventListener("resize", updateWidth);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", updateWidth);
    });

    return { shouldShowShortName };
  },
  methods: {
    selectProject(projectName: string) {
      this.selectedProject = projectName;
    },
    openLink(link: string | null) {
      if (link) {
        window.open(link, "_blank");
        window.URL.revokeObjectURL(link);
      }
    },
    toggleText() {
      // Bascule entre l'état réduit et étendu
      this.isExpanded = !this.isExpanded;
    },
    handleResize() {
      const el = this.$refs.resizeElement as HTMLElement;
      this.hasScrollBar = el.scrollHeight > el.clientHeight;
    },
  },
  mounted() {
    const element = this.$refs.resizeElement as HTMLElement;
    if (element) {
      this.observer = new ResizeObserver(this.handleResize);
      this.observer.observe(element);
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect(); // Nettoyer l'observer
    }
  },
});
</script>
<style scoped>
#project-list {
  position: absolute;
  z-index: 100;
  left: 157px;
  top: 101px;
  overflow-y: auto;
  border: solid 1px #ececec;
  border-radius: 5px;
  background: rgba(33, 33, 33, 0.8);
  backdrop-filter: blur(2px);
  box-shadow: 0 4px 15px rgba(33, 33, 33, 0.2);
  padding: 5px 10px;
  width: 109px;
}

#project-list.light {
  border-color: #212121;
  background: rgba(236, 236, 236, 0.8);
  box-shadow: 0 4px 15px rgba(236, 236, 236, 0.2);
}

.project {
  transition: opacity 0.3s ease, margin-left 1s ease-out;
  margin-bottom: 3px;
  margin-top: 3px;
  cursor: pointer;
}

.project.selected {
  margin-left: 22px;
}

#project-container {
  position: absolute;
  z-index: 100;
  right: 20px;
  bottom: 20px;
  overflow-y: auto;
  border: solid 1px #ececec;
  border-radius: 5px;
  background: rgba(33, 33, 33, 0.8);
  backdrop-filter: blur(2px);
  box-shadow: 0 4px 15px rgba(33, 33, 33, 0.2);
  padding: 6px 10px 10px 10px;
  width: calc(100% - 62px);
  max-height: calc(100% - 235px);
  display: flex;
  flex-direction: column;
  overflow: auto;
}

#project-container.hasScrollBar {
  padding-right: 2px;
  width: calc(100% - 54px);
}

#project-container.light {
  border-color: #212121;
  background: rgba(236, 236, 236, 0.8);
  box-shadow: 0 4px 15px rgba(236, 236, 236, 0.2);
}

.title {
  text-transform: uppercase;
  border-bottom: 1px solid;
  border-color: #ececec;
  margin-bottom: 10px;
  padding-bottom: 5px;
}

.title.light {
  border-color: #212121;
}

#screen {
  margin-bottom: 10px;
  cursor: pointer;
}

#screen:hover {
  cursor: pointer;
}

#more-description {
  height: 0px;
  overflow: hidden;
}

#more-description.extended {
  height: unset;
  overflow: unset;
}

#see-more {
  align-self: center;
  margin-top: 10px;
  cursor: pointer;
  opacity: 1;
  transition: opacity 0.3s ease-out;
}

#stack {
  margin-top: 10px;
  font-size: 14px;
}

@media (hover: hover) and (pointer: fine) {
  #see-more:hover {
    opacity: 0.25;
  }

  .project:hover {
    opacity: 0.25;
  }
}

@media screen and (orientation: landscape) {
  /* Styles pour les écrans en mode paysage */
  #project-container {
    max-height: unset;
    width: calc(100% - 349px);
    height: calc(100% - 62px);
  }

  #project-container.hasScrollBar {
    padding-right: 2px;
    width: calc(100% - 341px);
  }
}

@media (min-width: 768px) {
  #project-list {
    padding: 5px 15px;
    border-radius: 10px;
    left: 198px;
    top: 138px;
    width: 242px;
  }

  .project {
    margin-bottom: 6px;
    margin-top: 6px;
  }

  #project-container {
    bottom: 30px;
    right: 30px;
    border-radius: 10px;
    max-height: calc(100% - 349px);
    width: calc(100% - 101px);
    padding: 14px 20px;
  }

  #project-container.hasScrollBar {
    padding-right: 12px;
    width: calc(100% - 93px);
  }

  .title {
    font-size: 20px;
    letter-spacing: 1.5px;
    padding-bottom: 10px;
    margin-bottom: 20px;
  }

  #stack {
    font-size: 16px;
  }
}

@media (min-width: 768px) and (orientation: landscape) {
  #project-list {
    width: 242px;
    left: 30px;
    top: 281px;
  }

  #project-container {
    max-height: unset;
    width: calc(100% - 405px);
    height: unset;
    max-height: calc(100% - 90px);
  }

  #project-container.hasScrollBar {
    width: calc(100% - 393px);
  }
}

@media screen and (min-width: 1200px) {
  #project-list {
    width: 300px;
    top: 318px;
    left: 30px;
  }

  #project-container {
    max-width: 600px;
  }

  .title {
    font-size: 26px;
  }

  #stack {
    font-size: 22px;
  }
}
</style>
